/**
 * A summary line that folds a block of detail away, with none of the
 * accordion's frame.
 *
 * For a fold inside a table cell or a value that nests, where an
 * `AccordionItem` would draw a bordered row of its own. A list of sibling
 * sections is an `Accordion`; a panel that folds as a whole is `Panel
 * collapsible`.
 *
 * @module components/ui/Disclosure
 */

import React, { type ReactNode } from "react";

export interface DisclosureProps {
  /** The always-visible line that toggles the rest. */
  summary: ReactNode;
  /** `warning` for a fold that hides something to act on. */
  tone?: "primary" | "warning";
  /** Whether it starts open. */
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
  "data-testid"?: string;
}

const TONE_CLASS: Record<NonNullable<DisclosureProps["tone"]>, string> = {
  primary: "text-primary-strong",
  warning: "text-warning-strong",
};

/**
 * @example
 * ```tsx
 * <Disclosure summary="View 3 items"><ItemList items={items} /></Disclosure>
 * ```
 */
export function Disclosure({
  summary,
  tone = "primary",
  defaultOpen = false,
  children,
  className = "",
  "data-testid": testId,
}: DisclosureProps): React.ReactElement {
  return (
    <details className={className || undefined} open={defaultOpen} data-testid={testId}>
      <summary className={`cursor-pointer text-md font-medium ${TONE_CLASS[tone]}`}>
        {summary}
      </summary>
      {children}
    </details>
  );
}
